import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, FileText, Upload, Printer, Trash2,
    Ship, MapPin, Package, Calendar, User, Filter, ChevronLeft,
    ChevronRight, Eye, Clock, CheckCircle2, AlertCircle, ArrowDownZA, ArrowUpAZ, Mail,
    TrendingUp, Sparkles, Download, RefreshCw, FileCheck, ShieldCheck, Send, XCircle, RotateCcw
} from 'lucide-react';
import SearchInputWithClear from '@/components/ui/SearchInputWithClear';

const ReleaseOrderList = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [sortBy, setSortBy] = useState('recent');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);

    const hasActiveFilters =
        searchQuery !== '' ||
        statusFilter !== 'ALL' ||
        sortBy !== 'recent' ||
        sortDirection !== 'desc';

    const resetFilters = () => {
        setSearchQuery('');
        setStatusFilter('ALL');
        setSortBy('recent');
        setSortDirection('desc');
    };

    const releaseOrderSearchInputClass =
        'w-full pl-12 pr-9 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all';

    // Sample data for Release Orders
    const releaseOrderList = [
        {
            id: 'RO-2024-001',
            roNumber: 'RO-2024-001',
            fileRef: 'KHIO006882825',
            customerName: 'Global Imports Inc.',
            hblNumber: 'HBL2024001',
            mblNumber: 'MBL2024001',
            portOfLoad: 'SHANGHAI, CN',
            portOfLoadCode: 'CNSHA',
            portOfDischarge: 'LOS ANGELES, CA',
            portOfDischargeCode: 'USLAX',
            carrier: 'COSCO',
            containerType: '40HC',
            containerCount: 2,
            releaseType: 'Telex',
            status: 'released',
            releaseDate: '2024-03-28',
            requestDate: '2024-03-25',
        },
        {
            id: 'RO-2024-002',
            roNumber: 'RO-2024-002',
            fileRef: 'KHIO006882826',
            customerName: 'Pacific Trading Co.',
            hblNumber: 'HBL2024002',
            mblNumber: 'MBL2024002',
            portOfLoad: 'NINGBO, CN',
            portOfLoadCode: 'CNNGB',
            portOfDischarge: 'LONG BEACH, CA',
            portOfDischargeCode: 'USLGB',
            carrier: 'Maersk',
            containerType: '20GP',
            containerCount: 4,
            releaseType: 'Express',
            status: 'pending',
            releaseDate: null,
            requestDate: '2024-03-27',
        },
        {
            id: 'RO-2024-003',
            roNumber: 'RO-2024-003',
            fileRef: 'KHIO006882827',
            customerName: 'Euro Import Solutions',
            hblNumber: 'HBL2024003',
            mblNumber: 'MBL2024003',
            portOfLoad: 'ROTTERDAM, NL',
            portOfLoadCode: 'NLRTM',
            portOfDischarge: 'NEW YORK, NY',
            portOfDischargeCode: 'USNYC',
            carrier: 'MSC',
            containerType: '40GP',
            containerCount: 1,
            releaseType: 'Original',
            status: 'draft',
            releaseDate: null,
            requestDate: '2024-03-26',
        },
        {
            id: 'RO-2024-004',
            roNumber: 'RO-2024-004',
            fileRef: 'KHIO006882828',
            customerName: 'Atlantic Merchandise',
            hblNumber: 'HBL2024004',
            mblNumber: 'MBL2024004',
            portOfLoad: 'HAMBURG, DE',
            portOfLoadCode: 'DEHAM',
            portOfDischarge: 'HOUSTON, TX',
            portOfDischargeCode: 'USHOU',
            carrier: 'Hapag-Lloyd',
            containerType: '40HC',
            containerCount: 3,
            releaseType: 'Telex',
            status: 'rejected',
            releaseDate: null,
            requestDate: '2024-03-24',
        },
        {
            id: 'RO-2024-005',
            roNumber: 'RO-2024-005',
            fileRef: 'KHIO006882829',
            customerName: 'Asia Pacific Traders',
            hblNumber: 'HBL2024005',
            mblNumber: 'MBL2024005',
            portOfLoad: 'BUSAN, KR',
            portOfLoadCode: 'KRPUS',
            portOfDischarge: 'SEATTLE, WA',
            portOfDischargeCode: 'USSEA',
            carrier: 'ONE',
            containerType: '20GP',
            containerCount: 2,
            releaseType: 'Express',
            status: 'released',
            releaseDate: '2024-03-27',
            requestDate: '2024-03-22',
        }
    ];

    const summary = useMemo(() => ({
        total: releaseOrderList.length,
        released: releaseOrderList.filter(r => r.status === 'released').length,
        pending: releaseOrderList.filter(r => r.status === 'pending').length,
        draft: releaseOrderList.filter(r => r.status === 'draft').length,
        rejected: releaseOrderList.filter(r => r.status === 'rejected').length,
    }), [releaseOrderList]);

    const insightCards = useMemo(() => [
        {
            icon: FileCheck,
            title: "Total R/O",
            value: summary.total,
            trend: "+12% from last month",
            trendUp: true,
            bgGradient: "bg-gradient-to-br from-blue-500 to-blue-600",
        },
        {
            icon: ShieldCheck,
            title: "Released",
            value: summary.released,
            trend: "Cleared for pickup",
            trendUp: true,
            bgGradient: "bg-gradient-to-br from-emerald-500 to-emerald-600",
        },
        {
            icon: Clock,
            title: "Pending",
            value: summary.pending,
            trend: "Awaiting approval",
            trendUp: false,
            bgGradient: "bg-gradient-to-br from-amber-500 to-amber-600",
        },
        {
            icon: TrendingUp,
            title: "Release Rate",
            value: summary.total > 0 ? `${Math.round((summary.released / summary.total) * 100)}%` : '0%',
            trend: "+8% improvement",
            trendUp: true,
            bgGradient: "bg-gradient-to-br from-purple-500 to-purple-600",
        },
    ], [summary]);

    const AIInsightCard = ({ icon: Icon, title, value, trend, trendUp, bgGradient }) => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/15 transition-all cursor-pointer"
        >
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${bgGradient} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                    <p className="text-white/70 text-xs">{title}</p>
                    <p className="text-white text-xl font-bold">{value}</p>
                </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs">
                <span className={trendUp ? 'text-emerald-300' : 'text-amber-300'}>{trend}</span>
            </div>
        </motion.div>
    );

    const StatusBadge = ({ status }) => {
        const styles = {
            released: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800',
            pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
            draft: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
            rejected: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
        };

        const icons = {
            released: <CheckCircle2 className="w-3.5 h-3.5" />,
            pending: <Clock className="w-3.5 h-3.5" />,
            draft: <FileText className="w-3.5 h-3.5" />,
            rejected: <XCircle className="w-3.5 h-3.5" />
        };

        const labels = {
            released: 'Released',
            pending: 'Pending',
            draft: 'Draft',
            rejected: 'Rejected'
        };

        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[status]}`}>
                {icons[status]}
                {labels[status]}
            </span>
        );
    };

    const ReleaseTypeBadge = ({ type }) => {
        const styles = {
            Original: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
            Express: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
            Telex: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${styles[type] || 'bg-slate-100 text-slate-600'}`}>
                {type}
            </span>
        );
    };

    // Get flag URL from flagcdn
    const getFlagUrl = (countryCode) => {
        if (!countryCode) return null;
        return `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`;
    };

    const PortBadge = ({ port, code, type }) => {
        const countryCode = code?.substring(0, 2)?.toUpperCase();

        return (
            <div className="flex items-center gap-2">
                {countryCode && (
                    <div className="flex items-center gap-1.5 rounded px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700">
                        <img
                            src={getFlagUrl(countryCode)}
                            alt={countryCode}
                            className="w-5 h-4 object-cover rounded-sm"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            {countryCode}
                        </span>
                    </div>
                )}
                <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{port}</span>
                    {code && (
                        <span className={`text-xs font-mono ${type === 'pol' ? 'text-cyan-600 dark:text-cyan-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            [{code}]
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const ActionButton = ({ icon: Icon, label, onClick, variant = 'default' }) => {
        const variants = {
            default: 'bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-400',
            primary: 'bg-blue-100 hover:bg-blue-200 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400',
            warning: 'bg-amber-100 hover:bg-amber-200 text-amber-600 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 dark:text-amber-400',
            danger: 'bg-red-100 hover:bg-red-200 text-red-600 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400'
        };

        return (
            <button
                onClick={onClick}
                className={`p-2 rounded-lg transition-all duration-200 ${variants[variant]}`}
                title={label}
            >
                <Icon className="w-4 h-4" />
            </button>
        );
    };

    const filteredOrders = useMemo(() => {
        return releaseOrderList.filter(order => {
            const matchesSearch = !searchQuery ||
                order.roNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.hblNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.fileRef.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.carrier.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesStatus = statusFilter === 'ALL' ||
                order.status.toUpperCase() === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [releaseOrderList, searchQuery, statusFilter]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-6 space-y-6 min-h-screen"
            data-testid="release-order-page"
        >
            {/* Header with gradient */}
            <div className="bg-gradient-to-br from-cyan-600 via-teal-700 to-emerald-800 dark:from-cyan-800 dark:via-teal-900 dark:to-emerald-950 relative overflow-hidden rounded-xl">
                {/* Background decorations */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full"></div>
                    <div className="absolute top-20 -left-20 w-60 h-60 bg-white/5 rounded-full"></div>
                    <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-white/5 rounded-full"></div>
                </div>

                <div className="relative z-10 px-6 py-6">
                    {/* Title Row */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                <FileCheck className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white" data-testid="release-order-title">Release Order Management</h1>
                                <p className="text-cyan-100 text-sm">Create, manage, and track all release orders for import shipments</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/release-order/new')}
                                className="px-4 py-2.5 bg-white text-cyan-700 rounded-xl text-sm font-semibold hover:bg-cyan-50 transition-all flex items-center gap-2 shadow-lg"
                                data-testid="new-release-order-btn"
                            >
                                <Plus className="w-4 h-4" />
                                New Release Order
                            </button>
                            <button
                                onClick={() => navigate('/import-booking')}
                                className="px-4 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 transition-all flex items-center gap-2 shadow-lg"
                                data-testid="back-to-bookings-btn"
                            >
                                <Ship className="w-4 h-4" />
                                Import Bookings
                            </button>
                        </div>
                    </div>

                    {/* AI Analytics Cards */}
                    <div className="grid grid-cols-4 gap-4">
                        {insightCards.map((card, index) => (
                            <AIInsightCard
                                key={index}
                                icon={card.icon}
                                title={card.title}
                                value={card.value}
                                trend={card.trend}
                                trendUp={card.trendUp}
                                bgGradient={card.bgGradient}
                            />
                        ))}
                    </div>

                    {/* AI Insight Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4"
                    >
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <p className="text-white font-medium text-sm">AI Insight</p>
                            <p className="text-cyan-100 text-xs">2 release orders pending approval. Telex releases processed 40% faster than original B/L releases this month.</p>
                        </div>
                        <button className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors">
                            View Details
                        </button>
                    </motion.div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search Input */}
                    <SearchInputWithClear
                        placeholder="Search by R/O Number, File Ref, Customer, HBL, or Carrier..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery('')}
                        iconLeft="left-4"
                        inputClassName={releaseOrderSearchInputClass}
                        testId="search-release-order"
                    />

                    {/* Filter Controls */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-xl p-1">
                            {['ALL', 'RELEASED', 'PENDING', 'DRAFT', 'REJECTED'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => setStatusFilter(status)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${statusFilter === status
                                        ? 'bg-cyan-600 text-white shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                                    data-testid={`filter-${status.toLowerCase()}`}
                                >
                                    {status === 'ALL' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
                                </button>
                            ))}
                        </div>

                        <button className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium transition-all flex items-center gap-2">
                            <Filter className="w-4 h-4" />
                            More Filters
                        </button>

                        <div className="flex items-center gap-2">
                            <button onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}>
                                {sortDirection === 'asc' ? <ArrowUpAZ className="w-4 h-4 text-slate-400" /> : <ArrowDownZA className="w-4 h-4 text-slate-400" />}
                            </button>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-3 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            >
                                <option value="recent">Most Recent</option>
                                <option value="release_date">Release Date</option>
                                <option value="customer">Customer Name</option>
                            </select>
                        </div>

                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium transition-all"
                            >
                                <RotateCcw className="w-4 h-4" />
                                Reset
                            </button>
                        )}

                        <button className="p-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-all">
                            <RefreshCw className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                        </button>
                    </div>
                </div>

                {/* Results Summary */}
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Showing <span className="font-semibold text-slate-900 dark:text-white">{filteredOrders.length}</span> of{' '}
                        <span className="font-semibold text-slate-900 dark:text-white">{releaseOrderList.length}</span> release orders
                    </p>
                </div>
            </div>

            {/* Release Orders List */}
            <div className="space-y-4">
                <AnimatePresence>
                    {filteredOrders.map((order, index) => (
                        <motion.div
                            key={order.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 hover:shadow-md hover:border-cyan-200 dark:hover:border-cyan-800 transition-all duration-200 overflow-hidden group"
                            data-testid={`release-order-card-${order.id}`}
                        >
                            <div className="p-5">
                                {/* Top Row - R/O Number, Status, Release Type, Actions */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <button
                                            className="text-lg font-bold text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 flex items-center gap-2"
                                            data-testid={`view-ro-${order.id}`}
                                        >
                                            {order.roNumber}
                                        </button>
                                        <StatusBadge status={order.status} />
                                        <ReleaseTypeBadge type={order.releaseType} />
                                    </div>

                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ActionButton icon={Eye} label="View Details" variant="primary" onClick={() => {}} />
                                        <ActionButton icon={Printer} label="Print" variant="warning" onClick={() => {}} />
                                        <ActionButton icon={Send} label="Send Release" onClick={() => {}} />
                                        <ActionButton icon={Mail} label="Email" onClick={() => {}} />
                                        <ActionButton icon={Trash2} label="Delete" variant="danger" onClick={() => {}} />
                                    </div>
                                </div>

                                {/* Customer & File Info */}
                                <div className="flex items-center gap-8 mb-4">
                                    <div>
                                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs mb-1">
                                            <User className="w-3.5 h-3.5" />
                                            CUSTOMER
                                        </div>
                                        <p className="text-base font-semibold text-slate-900 dark:text-white">
                                            {order.customerName}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs mb-1">
                                            <FileText className="w-3.5 h-3.5" />
                                            FILE REF
                                        </div>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 font-mono">
                                            {order.fileRef}
                                        </p>
                                    </div>
                                </div>

                                {/* Route & Details Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                    {/* POL */}
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
                                            <Ship className="w-3.5 h-3.5" />
                                            PORT OF LOADING
                                        </div>
                                        <PortBadge port={order.portOfLoad} code={order.portOfLoadCode} type="pol" />
                                    </div>

                                    {/* Route Arrow */}
                                    <div className="hidden lg:flex items-center justify-center">
                                        <div className="w-full h-0.5 bg-gradient-to-r from-cyan-400 to-emerald-400 relative">
                                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-400 rotate-45" />
                                        </div>
                                    </div>

                                    {/* POD */}
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
                                            <MapPin className="w-3.5 h-3.5" />
                                            PORT OF DISCHARGE
                                        </div>
                                        <PortBadge port={order.portOfDischarge} code={order.portOfDischargeCode} type="pod" />
                                    </div>

                                    {/* HBL */}
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
                                            <FileCheck className="w-3.5 h-3.5" />
                                            HBL #
                                        </div>
                                        <span className="text-sm font-semibold text-slate-900 dark:text-white font-mono">
                                            {order.hblNumber}
                                        </span>
                                    </div>

                                    {/* MBL */}
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
                                            <FileText className="w-3.5 h-3.5" />
                                            MBL #
                                        </div>
                                        <span className="text-sm font-semibold text-slate-900 dark:text-white font-mono">
                                            {order.mblNumber}
                                        </span>
                                    </div>

                                    {/* Container */}
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
                                            <Package className="w-3.5 h-3.5" />
                                            CONTAINER
                                        </div>
                                        <span className="inline-flex items-center px-3 py-1.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 rounded-lg text-sm font-semibold">
                                            {order.containerCount}x {order.containerType}
                                        </span>
                                    </div>
                                </div>

                                {/* Bottom Row - Carrier, Dates */}
                                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                    <div className="flex items-center gap-6">
                                        <div>
                                            <span className="text-xs text-slate-500 dark:text-slate-400">Carrier</span>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-white">{order.carrier}</p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-500 dark:text-slate-400">Request Date</span>
                                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{order.requestDate}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xs text-slate-500 dark:text-slate-400">Release Date</span>
                                        <p className={`text-sm font-bold ${order.releaseDate ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                            {order.releaseDate || 'Pending'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    Page {currentPage} of 1
                </p>
                <div className="flex items-center gap-2">
                    <button className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50" disabled>
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button className="px-3 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium">1</button>
                    <button className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50" disabled>
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default ReleaseOrderList;
